import { api, type APIKey, type KeyStatus, type TaskStatus } from "./client";

// --- 运营端供应商管理域(P5,doc/049)---
// 与供应商端 /api/v1/supplier/* 对称:运营端 /api/v1/suppliers 复数,全量不脱敏。

export interface OpsSupplier {
  id: number;
  user_id: number | null;
  account_id: number;
  username: string;
  contact_name: string;
  contact_email: string;
  distribute_limit: number;
  code_prefix: string;
  enabled: boolean;
  target_count: number;
  created_at: string;
  updated_at: string;
}

export interface OpsSupplierKeyCreateBody {
  name: string;
  key: string;
  supplier_field?: string;
  base_url?: string;
  models?: string;
  unit_price?: number;
  protocol_type?: number;
  expires_at?: string;
  status?: KeyStatus;
  remark?: string;
}

export type FingerprintConflictStatus = "open" | "resolved";
export type FingerprintConflictConclusion =
  | "confirmed_existing_owner"
  | "allow_submitter_retry"
  | "no_conflict";

export interface FingerprintConflictKey {
  key_id: number;
  name: string;
  owner: string;
  sites: string[];
}

export interface FingerprintConflict {
  conflict_ref: string;
  status: FingerprintConflictStatus;
  opened_at: string;
  opened_by_type: string;
  opened_by_id?: number | null;
  opened_by_name?: string;
  submitter?: string;
  submitted_key?: string;
  target_sites?: string[];
  conflicting?: string;
  fingerprint?: string;
  conflicting_keys?: FingerprintConflictKey[];
  summary: string;
  resolved_at?: string | null;
  resolution?: string;
}

export interface OpsPublishTarget {
  supplier_id: number;
  supplier_username: string;
  supplier_enabled: boolean;
  publishable: boolean;
  target_code: string;
  code_source: "explicit" | "automatic";
  site_id: number;
  site_type: "relay" | "push";
  site_code: string;
  site_name: string;
  templates: Array<{ id: number; code: string; name: string }>;
}

export interface OpsAuditEntry {
  id: number;
  operator_id: number;
  operator_name: string;
  action: string;
  target_type: string;
  target_id: string;
  target_key: string;
  before_state?: string | null;
  after_state?: string | null;
  ip: string;
  user_agent: string;
  note: string;
  trace_id: string;
  occurred_at: string;
}

export interface OpsRecord {
  id: number;
  key_id: number;
  key_name: string;
  operation_id?: string | null;
  site_id: number;
  site_type: "relay" | "push";
  site_code: string;
  site_name: string;
  target_code?: string;
  status: TaskStatus;
  error_message: string;
  remote_channel_id?: number | null;
  retry_count: number;
  max_retries: number;
  created_at: string;
  completed_at?: string | null;
  updated_at: string;
}

export interface OpsSupplierCreateBody {
  username: string;
  password: string;
  contact_name?: string;
  contact_email?: string;
  distribute_limit?: number;
  code_prefix?: string;
  enabled?: boolean;
}

export interface OpsSupplierUpdateBody {
  contact_name?: string;
  contact_email?: string;
  distribute_limit?: number;
  code_prefix?: string;
  password?: string;
  enabled?: boolean;
}

export interface OpsPaged<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

function qs(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const opsSupplierApi = {
  list: (params?: { q?: string; enabled?: boolean; page?: number; page_size?: number }) =>
    api.get<OpsPaged<OpsSupplier>>(`/suppliers${qs(params)}`),

  create: (body: OpsSupplierCreateBody) => api.post<OpsSupplier>("/suppliers", body),

  get: (id: number) => api.get<OpsSupplier>(`/suppliers/${id}`),

  update: (id: number, body: OpsSupplierUpdateBody) =>
    api.put<OpsSupplier>(`/suppliers/${id}`, body),

  disable: (id: number) => api.del<{ deleted: number; soft: boolean }>(`/suppliers/${id}`),

  listPublishTargets: (id: number) =>
    api.get<{ items: OpsPublishTarget[]; total: number }>(`/suppliers/${id}/publish-targets`),

  listAudit: (id: number, params?: { action?: string; from?: string; to?: string; page?: number; page_size?: number }) =>
    api.get<OpsPaged<OpsAuditEntry>>(`/suppliers/${id}/audit${qs(params)}`),

  listRecords: (id: number, params?: { status?: TaskStatus; page?: number; page_size?: number }) =>
    api.get<OpsPaged<OpsRecord>>(`/suppliers/${id}/records${qs(params)}`),

  createKey: (id: number, body: OpsSupplierKeyCreateBody) =>
    api.post<APIKey>(`/suppliers/${id}/keys`, body),

  listFingerprintConflicts: (
    params: { status?: "open" | "resolved" | "all"; page?: number; page_size?: number } = {}
  ) => api.get<OpsPaged<FingerprintConflict>>(`/suppliers/key-fingerprint-conflicts${qs(params)}`),

  resolveFingerprintConflict: (
    conflictRef: string,
    body: { conclusion: FingerprintConflictConclusion; reason: string }
  ) => api.post<{ conflict_ref: string; status: "resolved" }>(
    `/suppliers/key-fingerprint-conflicts/${conflictRef}/resolve`,
    body
  ),
};
